import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ProductDataFilenameConventionService } from 'src/app/services/product-data-filename-conventions/product-data-filename-convention.service';
import {
  OTHER_DETAIL_PATTERN,
  ProductDataFilenameConvention,
  ProductDataFilenameConventionFilename,
  ProductDataFilenameConventionWriteReason,
  USER_SELECTABLE_WRITE_REASON_KINDS,
  UpdateWriteReasonKind,
  writeReasonLabel,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';

export interface UpdateProductDataFilenameConventionDialogData {
  organizationId: string;
  current: ProductDataFilenameConvention;
}

// Update dialog — hosts shared form + required Reason toggle + conditional
// Detail input (visible + required only when Reason=Other, ≥3 alphabetic chars).
@Component({
  selector: 'app-update-product-data-filename-convention-dialog',
  templateUrl: './update-product-data-filename-convention-dialog.component.html',
  styleUrls: ['./update-product-data-filename-convention-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class UpdateProductDataFilenameConventionDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectableReasons: readonly UpdateWriteReasonKind[] = USER_SELECTABLE_WRITE_REASON_KINDS;
  readonly reasonLabel = writeReasonLabel;

  readonly reasonControl = new FormControl<UpdateWriteReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(OTHER_DETAIL_PATTERN)],
  });

  readonly seedValue: ProductDataFilenameConventionFilename;
  currentValue: ProductDataFilenameConventionFilename;
  formIsValid = true; // hydrated with the prior row → treated as valid until user re-picks

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpdateProductDataFilenameConventionDialogData,
    private readonly dialogRef: MatDialogRef<
      UpdateProductDataFilenameConventionDialogComponent,
      ProductDataFilenameConvention | undefined
    >,
    private readonly service: ProductDataFilenameConventionService,
  ) {
    this.seedValue = { ...data.current.filename, extensions: [...data.current.filename.extensions] };
    this.currentValue = { ...this.seedValue };
  }

  get isOther(): boolean {
    return this.reasonControl.value === 'other';
  }

  get canSave(): boolean {
    if (!this.formIsValid) return false;
    if (this.reasonControl.value === null) return false;
    if (this.isOther && !this.detailControl.valid) return false;
    return true;
  }

  onFormValue(value: ProductDataFilenameConventionFilename): void {
    this.currentValue = value;
  }

  onFormValid(valid: boolean): void {
    this.formIsValid = valid;
  }

  save(): void {
    if (!this.canSave) return;
    const writeReason = this.buildWriteReason();
    if (writeReason === null) return;
    this.service
      .create(this.data.organizationId, {
        filename: this.currentValue,
        writeReason,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ProductDataFilenameConvention) => this.dialogRef.close(result));
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildWriteReason(): ProductDataFilenameConventionWriteReason | null {
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') {
      return { kind: 'other', detail: this.detailControl.value };
    }
    return { kind };
  }
}
